import { useTranslation } from 'react-i18next';
import { useDashboardQuery } from './useDashboard';
import { HomeKpiTile } from './HomeKpiTile';

/**
 * EAA-1814 (S3 rebuild — canonical frame 09): the home dashboard's KPI row of
 * four — Tổ chức, Tài khoản hoạt động, Ứng dụng đang chạy, Liên kết vai trò
 * (frame 09 lines 448-624) — now driven by the single `GET /dashboard`
 * aggregate (EAA-1829) instead of three separate S2 fetches
 * (`fetchOrganizations` + `fetchRoleMapSyncStatus` + `fetchAdminApps`).
 *
 * Every tile stays its own capability gate (no `console.home.*` capability
 * exists — CLAUDE.md rule, restated in the ticket): a plain user with none of
 * the four capabilities below sees no KPI row at all, not an empty grid.
 *
 * WHAT THE ENDPOINT DOES NOT RETURN (confirmed by reading `DashboardService` —
 * see `useDashboard.ts`'s file comment): no month-over-month delta for any
 * KPI. The frame's "+18,4% so với tháng trước" style sub-lines are NOT
 * reproduced — inventing a percentage with no backing field would violate the
 * "never invented" rule. Each `sub` below is instead either a real derived
 * number from the SAME payload (e.g. "Ứng dụng đang chạy"'s "N app chờ duyệt"
 * is `total - enabled`, not a guess) or a static factual caption.
 */
export function HomeKpiStrip({
    can,
    apiBase,
    accessToken,
    authenticated,
}: {
    can: (key: string) => boolean;
    apiBase: string;
    accessToken: string | null;
    authenticated: boolean;
}) {
    const { t } = useTranslation();
    const canOrgs = can('console.orgs.view');
    const canAccounts = can('console.accounts.issue');
    const canApps = can('console.apps.view');
    const canRoleMap = can('console.rolemap.view');

    const baseEnabled = authenticated && Boolean(accessToken);
    const dashboardQuery = useDashboardQuery(
        apiBase,
        accessToken,
        baseEnabled && (canOrgs || canAccounts || canApps || canRoleMap)
    );

    if (!canOrgs && !canAccounts && !canApps && !canRoleMap) {
        return null;
    }

    const data = dashboardQuery.data;
    const loading = dashboardQuery.isLoading;
    const errorNote = dashboardQuery.isError ? t('home.kpiStrip.errorNote') : undefined;
    const retry = () => dashboardQuery.refetch();

    // "Liên kết vai trò" (4th tile): the endpoint has no role-map total/ratio
    // field at all (see useDashboard.ts) — its value is the REAL open-issue
    // count (paused links + orphan Levels from `attention`, both already
    // real aggregates), not a fabricated "linked / total" ratio. Zero issues
    // is a legitimately real zero, not an "unavailable" placeholder.
    const pausedCount = data?.attention.find((a) => a.type === 'role_map_paused')?.count ?? 0;
    const orphanCount = data?.attention.find((a) => a.type === 'orphan_level')?.count ?? 0;
    const roleMapIssues = pausedCount + orphanCount;
    const roleMapSub =
        roleMapIssues > 0
            ? t('home.kpiStrip.roleMapIssuesSub', { paused: pausedCount, orphan: orphanCount })
            : t('home.kpiStrip.noIssues');

    const appsData = data?.kpis.applications;
    const appsPending = appsData ? appsData.total - appsData.enabled : 0;
    const appsSub = appsData
        ? appsPending > 0
            ? t('home.kpiStrip.appsPendingSub', { count: appsPending })
            : t('home.kpiStrip.appsApprovedAll')
        : undefined;

    return (
        <section className="portal-home-section">
            <div className="portal-home-kpi-grid">
                {canOrgs && (
                    <HomeKpiTile
                        label={t('home.kpiStrip.orgsLabel')}
                        icon={<BuildingIcon />}
                        to="/organizations"
                        loading={loading}
                        value={data?.kpis.organizations.total}
                        // No time-series field backs a "so với tháng trước" delta
                        // (see useDashboard.ts) — a factual caption replaces it
                        // rather than an invented percentage.
                        sub={data ? t('home.kpiStrip.orgsSub') : undefined}
                        errorNote={errorNote}
                        onRetry={retry}
                    />
                )}
                {canAccounts && (
                    <HomeKpiTile
                        label={t('home.kpiStrip.accountsLabel')}
                        icon={<UserIcon />}
                        loading={loading}
                        value={data?.kpis.accounts.total}
                        sub={data ? t('home.kpiStrip.accountsSub') : undefined}
                        errorNote={errorNote}
                        onRetry={retry}
                    />
                )}
                {canApps && (
                    // EAA-1814: label restored to the design's own wording verbatim
                    // per the ticket ("the design is authoritative for copy"). A
                    // prior pass changed this to "Đã duyệt" on the factual grounds
                    // that `enabled` is an approval gate, not a liveness signal —
                    // that reasoning still stands as a note, but the visible copy
                    // must match frame 09 (line 543), so it is reverted here.
                    <HomeKpiTile
                        label={t('home.kpiStrip.appsLabel')}
                        icon={<GridIcon />}
                        to="/console/apps"
                        loading={loading}
                        value={
                            appsData ? (
                                <>
                                    {appsData.enabled}
                                    <small className="portal-home-kpi-value-of"> / {appsData.total}</small>
                                </>
                            ) : undefined
                        }
                        sub={appsSub}
                        errorNote={errorNote}
                        onRetry={retry}
                    />
                )}
                {canRoleMap && (
                    <HomeKpiTile
                        label={t('home.kpiStrip.roleMapLabel')}
                        icon={<AlertIcon />}
                        to="/console/role-map"
                        loading={loading}
                        value={data ? roleMapIssues : undefined}
                        sub={data ? roleMapSub : undefined}
                        tone={data ? (roleMapIssues > 0 ? 'alert' : 'ok') : 'default'}
                        errorNote={errorNote}
                        onRetry={retry}
                    />
                )}
            </div>
        </section>
    );
}

export default HomeKpiStrip;

/* Inline icons — no icon dependency (design-pass rule), matching frame 09's
   40x40 tile icon circle (lines ~460-467, ~508-513, ~549-558, ~596-603). */
function BuildingIcon() {
    return (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path
                d="M3 21h18M6 21V5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v16M14 21V9h3a1 1 0 0 1 1 1v11"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}
function UserIcon() {
    return (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <circle cx="12" cy="8" r="3.5" />
            <path d="M5 20a7 7 0 0 1 14 0" strokeLinecap="round" />
        </svg>
    );
}
function GridIcon() {
    return (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <rect x="3" y="3" width="7" height="7" rx="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
    );
}
function AlertIcon() {
    return (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M12 8v5m0 3.5h.01" strokeLinecap="round" />
            <circle cx="12" cy="12" r="9" />
        </svg>
    );
}
